/**
 * chrz-theme JavaScript entry.
 *
 * Alpine and htmx are already on window.Chrz from the core bootstrap.
 * This file adds theme-specific component data and Chart.js initialisation.
 */

import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
} from 'chart.js';

// Cart & checkout progressive enhancements (quantity steppers).
import './checkout.js';

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
);

// ─── Theme helper ─────────────────────────────────────────────────────────────
// chrzThemeToggle() is registered globally in header.php (must run before Alpine.start()).
// This helper is just for the chart's theme-aware rendering below.
function resolveCurrentTheme() {
  const stored = document.documentElement.dataset.theme;
  if (stored === 'dark' || stored === 'light') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// ─── Performance Chart ────────────────────────────────────────────────────────
function initPerformanceChart() {
  const canvas = document.getElementById('chrz-performance-chart');
  if (!canvas) return;

  const isDark = resolveCurrentTheme() === 'dark';

  // Persian month names, keyed by the MM part of a Jalali "YYYY-MM-DD" label.
  const persianMonths = {
    '01': 'فروردین', '02': 'اردیبهشت', '03': 'خرداد', '04': 'تیر',
    '05': 'مرداد',   '06': 'شهریور',   '07': 'مهر',   '08': 'آبان',
    '09': 'آذر',     '10': 'دی',       '11': 'بهمن',  '12': 'اسفند',
  };

  // Cumulative % return — daily, 1404-07-01 → 1405-03-19 (Jalali dates).
  const labels = ["1404-07-01","1404-07-02","1404-07-05","1404-07-06","1404-07-07","1404-07-08","1404-07-09","1404-07-12","1404-07-13","1404-07-14","1404-07-15","1404-07-16","1404-07-19","1404-07-20","1404-07-21","1404-07-22","1404-07-23","1404-07-26","1404-07-27","1404-07-28","1404-07-29","1404-07-30","1404-08-03","1404-08-04","1404-08-05","1404-08-06","1404-08-07","1404-08-10","1404-08-11","1404-08-12","1404-08-13","1404-08-14","1404-08-17","1404-08-18","1404-08-19","1404-08-20","1404-08-21","1404-08-24","1404-08-25","1404-08-26","1404-08-27","1404-08-28","1404-09-01","1404-09-02","1404-09-04","1404-09-05","1404-09-08","1404-09-09","1404-09-10","1404-09-11","1404-09-12","1404-09-15","1404-09-16","1404-09-17","1404-09-18","1404-09-19","1404-09-22","1404-09-23","1404-09-24","1404-09-25","1404-09-26","1404-09-29","1404-09-30","1404-10-01","1404-10-02","1404-10-03","1404-10-06","1404-10-07","1404-10-08","1404-10-09","1404-10-10","1404-10-14","1404-10-15","1404-10-16","1404-10-17","1404-10-20","1404-10-21","1404-10-22","1404-10-23","1404-10-24","1404-10-28","1404-10-29","1404-10-30","1404-11-01","1404-11-04","1404-11-05","1404-11-06","1404-11-07","1404-11-08","1404-11-11","1404-11-12","1404-11-13","1404-11-14","1404-11-18","1404-11-19","1404-11-20","1404-11-21","1404-11-25","1404-11-26","1404-11-27","1404-11-28","1404-11-29","1404-12-02","1404-12-03","1404-12-04","1404-12-05","1404-12-06","1404-12-12","1404-12-13","1404-12-16","1404-12-17","1404-12-18","1404-12-19","1404-12-23","1404-12-24","1404-12-25","1404-12-26","1404-12-27","1405-01-05","1405-01-08","1405-01-09","1405-01-10","1405-01-11","1405-01-15","1405-01-16","1405-01-17","1405-01-18","1405-01-19","1405-01-22","1405-01-23","1405-01-24","1405-01-26","1405-01-29","1405-01-30","1405-01-31","1405-02-01","1405-02-02","1405-02-05","1405-02-06","1405-02-07","1405-02-08","1405-02-09","1405-02-12","1405-02-13","1405-02-14","1405-02-15","1405-02-16","1405-02-19","1405-02-20","1405-02-21","1405-02-22","1405-02-23","1405-02-26","1405-02-27","1405-02-28","1405-02-29","1405-02-30","1405-03-02","1405-03-03","1405-03-04","1405-03-05","1405-03-09","1405-03-10","1405-03-11","1405-03-12","1405-03-13","1405-03-16","1405-03-17","1405-03-18","1405-03-19"];

  const series = {
    highRisk:    [0, -1, 2, 6, 9, 11, 12, 16, 19, 21, 24, 23, 25, 29, 31, 29, 26, 31, 33, 31, 30, 32, 31, 33, 36, 39, 39, 39, 39, 41, 39, 35, 35, 31, 28, 27, 28, 30, 32, 33, 31, 31, 32, 35, 39, 38, 38, 38, 42, 43, 44, 49, 48, 48, 49, 50, 55, 55, 59, 61, 59, 65, 69, 68, 71, 77, 81, 86, 86, 80, 81, 80, 86, 93, 97, 94, 98, 104, 104, 97, 102, 103, 102, 104, 107, 107, 110, 115, 121, 117, 116, 111, 114, 114, 117, 121, 121, 119, 121, 120, 119, 121, 122, 122, 121, 121, 120, 120, 120, 120, 120, 120, 120, 120, 112, 106, 110, 115, 113, 113, 111, 111, 111, 115, 115, 115, 118, 116, 115, 116, 117, 115, 113, 114, 113, 113, 114, 118, 126, 132, 132, 133, 137, 148, 152, 148, 148, 148, 151, 158, 162, 160, 161, 157, 151, 149, 147, 144, 137, 144, 143, 145, 146, 145, 143, 142, 140, 142, 139, 142],
    lowRisk:     [0, 2, 4, 3, 4, 5, 6, 6, 6, 6, 7, 9, 9, 8, 9, 8, 9, 9, 10, 9, 9, 8, 8, 9, 9, 8, 9, 10, 10, 10, 10, 10, 10, 9, 10, 12, 14, 14, 13, 14, 14, 15, 15, 15, 16, 16, 18, 20, 22, 21, 23, 24, 25, 25, 23, 22, 26, 26, 27, 25, 27, 27, 27, 29, 32, 36, 39, 42, 36, 34, 35, 35, 37, 38, 38, 38, 39, 40, 40, 39, 40, 40, 40, 41, 42, 43, 44, 46, 49, 47, 47, 45, 46, 46, 48, 49, 50, 49, 50, 49, 49, 50, 51, 50, 50, 50, 50, 50, 50, 50, 50, 50, 51, 51, 47, 45, 47, 49, 49, 49, 48, 49, 49, 52, 52, 52, 54, 51, 50, 52, 54, 52, 50, 51, 51, 51, 52, 54, 55, 58, 61, 65, 67, 71, 71, 69, 67, 68, 69, 72, 72, 71, 71, 71, 69, 67, 67, 65, 61, 64, 63, 63, 64, 65, 65, 65, 63, 66, 64, 65],
    gold:        [0, 5, 7, 4, 6, 8, 11, 9, 9, 8, 10, 15, 13, 10, 11, 11, 12, 10, 12, 11, 11, 9, 9, 11, 9, 6, 8, 9, 9, 9, 10, 9, 9, 9, 10, 14, 17, 17, 16, 17, 17, 18, 17, 18, 18, 19, 22, 25, 28, 26, 29, 31, 32, 32, 29, 28, 34, 33, 35, 30, 34, 34, 33, 37, 42, 47, 52, 57, 46, 46, 43, 46, 56, 64, 62, 51, 56, 59, 57, 58, 55, 56, 55, 60, 65, 66, 73, 84, 97, 88, 86, 75, 81, 81, 87, 96, 96, 92, 96, 94, 93, 95, 99, 97, 95, 95, 92, 92, 92, 92, 92, 92, 92, 92, 73, 60, 69, 81, 75, 75, 70, 70, 71, 79, 78, 79, 86, 81, 77, 81, 83, 79, 74, 75, 74, 72, 76, 76, 77, 83, 96, 110, 117, 120, 116, 108, 104, 107, 106, 113, 111, 107, 103, 102, 97, 94, 97, 92, 83, 88, 84, 82, 83, 88, 89, 89, 85, 89, 86, 86],
    salam:       [0, 0, 3, 5, 7, 7, 7, 10, 12, 12, 14, 13, 15, 17, 17, 17, 16, 19, 19, 19, 19, 20, 19, 20, 22, 24, 23, 24, 24, 25, 24, 23, 23, 21, 21, 20, 20, 21, 23, 22, 23, 23, 24, 26, 29, 27, 27, 27, 29, 29, 32, 35, 35, 35, 36, 36, 40, 41, 43, 44, 43, 48, 50, 50, 52, 56, 57, 60, 61, 56, 58, 58, 62, 67, 68, 66, 69, 72, 72, 67, 72, 73, 74, 73, 68, 63, 58, 55, 59, 57, 60, 62, 61, 66, 67, 62, 63, 62, 57, 55, 57, 57, 52, 48, 51, 50, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 51, 46, 42, 46, 50, 54, 57, 61, 66, 70, 69, 67, 68, 73, 68, 73],
    fixedIncome: [0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3, 3, 3, 3, 3, 3, 3, 4, 4, 4, 4, 4, 4, 4, 4, 5, 5, 5, 5, 5, 5, 5, 6, 6, 6, 6, 6, 6, 6, 7, 7, 7, 7, 7, 7, 7, 7, 8, 8, 8, 8, 8, 8, 8, 9, 9, 9, 9, 9, 9, 9, 10, 10, 10, 10, 10, 10, 10, 11, 11, 11, 11, 11, 11, 11, 12, 12, 12, 12, 12, 12, 13, 13, 13, 13, 13, 13, 14, 14, 14, 14, 14, 14, 14, 15, 15, 15, 15, 15, 15, 16, 16, 16, 16, 17, 17, 17, 18, 18, 18, 18, 18, 18, 19, 19, 19, 19, 19, 20, 20, 20, 20, 20, 20, 20, 21, 21, 21, 21, 21, 21, 22, 22, 22, 22, 22, 22, 22, 23, 23, 23, 23, 23, 23, 24, 24, 24, 24, 24, 24, 25, 25, 25, 25, 25, 25, 26, 26],
    dollar:      [0, 3, 8, 6, 7, 9, 11, 10, 8, 6, 9, 10, 7, 7, 6, 4, 6, 2, 4, 2, 3, 4, 2, 3, 3, 2, 2, 4, 3, 3, 3, 3, 3, 3, 3, 5, 7, 8, 7, 7, 8, 8, 8, 8, 8, 8, 11, 12, 14, 14, 15, 17, 18, 20, 19, 19, 23, 24, 24, 24, 26, 26, 25, 26, 29, 29, 33, 37, 31, 33, 29, 32, 38, 40, 40, 34, 37, 35, 35, 36, 33, 35, 33, 34, 35, 36, 38, 46, 52, 56, 52, 47, 51, 48, 52, 54, 56, 52, 53, 53, 54, 55, 57, 56, 56, 57, 58, 58, 58, 50, 45, 44, 40, 41, 39, 38, 41, 47, 51, 52, 52, 52, 52, 52, 50, 51, 54, 48, 47, 50, 52, 46, 41, 42, 46, 47, 47, 50, 49, 52, 59, 69, 75, 79, 76, 73, 68, 69, 68, 73, 72, 71, 72, 73, 72, 70, 72, 69, 65, 66, 64, 63, 64, 67, 67, 66, 67, 70, 69, 68],
  };

  // On mobile, page the chart 3 months at a time: the canvas is widened to
  // (number of 3-month pages × viewport width) and the wrapper snap-scrolls,
  // so swiping the page reveals the next/previous months. Desktop fills its box.
  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  const wrapEl = canvas.closest('.chrz-performance__chart-wrap');
  const scrollEl = canvas.closest('.chrz-performance__chart-scroll');
  const monthCount = new Set(labels.map((d) => d.slice(0, 7))).size;
  let xMaxTicks = 8;

  if (isMobile && wrapEl && scrollEl) {
    const pages = Math.max(1, Math.ceil(monthCount / 3));
    const viewport = wrapEl.clientWidth;

    // Force the scroll track to the full multi-page width — otherwise it stays
    // clamped to the viewport (it lives inside an overflow-x container) and the
    // whole chart would be squeezed into the first page.
    scrollEl.style.width = `${pages * viewport}px`;

    // Equal-width page spacers provide the scroll-snap stops; the canvas is
    // overlaid across the full width via CSS (position: absolute; inset: 0).
    for (let i = 0; i < pages; i++) {
      const page = document.createElement('div');
      page.className = 'chrz-performance__chart-page';
      page.style.width = `${viewport}px`;
      scrollEl.appendChild(page);
    }

    // ~one tick per month so each 3-month page shows three labelled months.
    xMaxTicks = Math.max(monthCount, 3);
  }

  const colors = {
    highRisk: isDark ? '#FEFDEE' : '#014542',  // Cheraz high-risk — hero line
    lowRisk:  isDark ? '#7FD1B9' : '#0E8C7A',  // Cheraz low-risk — teal-green
    gold:     '#DEC79E',                        // Sand Gold
    salam:    isDark ? '#E0A36A' : '#C97B5A',  // Salam — terracotta
    fixed:    '#8a9577',                        // Fixed income — muted sage benchmark
    dollar:   isDark ? '#9FB6D4' : '#263C53',  // Dollar — navy / soft blue
  };

  const gridColor  = isDark ? 'rgb(200 201 172 / 18%)' : 'rgb(200 201 172 / 60%)';
  const zeroColor  = isDark ? 'rgb(200 201 172 / 35%)' : 'rgb(38 60 83 / 35%)';
  const labelColor = isDark ? 'rgb(200 201 172 / 70%)' : '#8a9577';
  const font       = { family: "'PelakFA', 'Vazirmatn', system-ui", size: 11 };

  const chart = new Chart(canvas, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'پرتفوی پر ریسک',
          data: series.highRisk,
          borderColor: colors.highRisk,
          backgroundColor: isDark ? 'rgba(254,253,238,0.08)' : 'rgba(1,69,66,0.08)',
          fill: true,
          tension: 0.4,
          borderWidth: 3,
          pointRadius: 0,
          pointHitRadius: 12,
          order: 1,
        },
        {
          label: 'پرتفوی کم ریسک',
          data: series.lowRisk,
          borderColor: colors.lowRisk,
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 0,
          pointHitRadius: 10,
          order: 2,
        },
        {
          label: 'طلا',
          data: series.gold,
          borderColor: colors.gold,
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.4,
          borderWidth: 1.6,
          pointRadius: 0,
          pointHitRadius: 10,
          order: 3,
        },
        {
          label: 'سلام',
          data: series.salam,
          borderColor: colors.salam,
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.4,
          borderWidth: 1.6,
          pointRadius: 0,
          pointHitRadius: 10,
          order: 4,
        },
        {
          label: 'دلار',
          data: series.dollar,
          borderColor: colors.dollar,
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.4,
          borderWidth: 1.6,
          pointRadius: 0,
          pointHitRadius: 10,
          order: 5,
        },
        {
          label: 'درآمد ثابت',
          data: series.fixedIncome,
          borderColor: colors.fixed,
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.4,
          borderWidth: 1.5,
          borderDash: [5, 4],
          pointRadius: 0,
          pointHitRadius: 10,
          order: 6,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 900, easing: 'easeInOutQuart' },
      plugins: {
        legend: { display: false },
        tooltip: {
          rtl: true,
          displayColors: true,
          boxWidth: 10,
          boxHeight: 10,
          cornerRadius: 8,
          padding: 12,
          bodyFont: { ...font, weight: '700' },
          titleFont: { ...font, weight: '700' },
          // Semi-transparent so the chart line stays visible behind the box.
          backgroundColor: isDark ? 'rgba(38,60,83,0.55)' : 'rgba(255,255,255,0.55)',
          borderColor: isDark ? 'rgba(200,201,172,0.35)' : 'rgba(200,201,172,0.6)',
          borderWidth: 1,
          titleColor: isDark ? '#FEFDEE' : '#013b39',
          bodyColor: isDark ? '#FEFDEE' : '#1c2e40',
          callbacks: {
            title(items) {
              return chrzToPersianDigits(items[0].label.replace(/-/g, '/'));
            },
            label(ctx) {
              const v    = ctx.parsed.y;
              const sign = v >= 0 ? '+' : '';
              return chrzToPersianDigits(` ${ctx.dataset.label}: ${sign}${v}٪`);
            },
          },
        },
      },
      scales: {
        x: {
          grid: { color: gridColor, lineWidth: 1, drawTicks: false },
          ticks: {
            color: labelColor,
            font,
            maxTicksLimit: xMaxTicks,
            autoSkip: true,
            maxRotation: 0,
            padding: 8,
            callback(value) {
              // value is the category index → resolve to the "YYYY-MM-DD" label,
              // then show its Persian month name for a clean axis.
              const label = this.getLabelForValue(value);
              const month = typeof label === 'string' ? label.slice(5, 7) : '';
              return persianMonths[month] || chrzToPersianDigits(label);
            },
          },
          border: { display: false },
        },
        y: {
          display: true,
          position: 'right',
          min: 0,
          beginAtZero: true,
          grid: {
            color(ctx) {
              // Highlight the zero baseline
              return ctx.tick.value === 0 ? zeroColor : 'transparent';
            },
            lineWidth(ctx) {
              return ctx.tick.value === 0 ? 1.5 : 0;
            },
            drawTicks: false,
          },
          ticks: {
            // Hide the right-side percentage labels on mobile.
            display: !isMobile,
            color: labelColor,
            font,
            maxTicksLimit: 5,
            padding: 8,
            callback: (v) => chrzToPersianDigits(v > 0 ? `+${v}٪` : `${v}٪`),
          },
          border: { display: false },
        },
      },
      interaction: {
        intersect: false,
        mode: 'index',
      },
    },
  });

  // Sync the static HTML legend dots with the chart line colors (theme-aware).
  // Matched by data-series key so the legend DOM can be reordered/grouped freely.
  const seriesKeys = ['highRisk', 'lowRisk', 'gold', 'salam', 'dollar', 'fixed'];
  const colorByKey = {};
  chart.data.datasets.forEach((dataset, i) => {
    colorByKey[seriesKeys[i]] = dataset.borderColor;
  });
  document
    .querySelectorAll('.chrz-performance__legend .chrz-legend-item__dot')
    .forEach((dot) => {
      const color = colorByKey[dot.dataset.series];
      if (color) dot.style.backgroundColor = color;
    });
}

// ─── Market Ticker — drag to scroll ─────────────────────────────────────────────
// Takes over the CSS marquee so the strip auto-scrolls AND can be dragged
// left/right with the mouse (grab cursor). Both copies share one transform.
function initMarketTicker() {
  const ticker = document.querySelector('.chrz-ticker');
  if (!ticker) return;

  const track = ticker.querySelector('.chrz-ticker__track');
  const lists = track ? track.querySelectorAll('.chrz-ticker__list') : [];
  if (!track || lists.length === 0) return;

  // Disable the CSS animation; we drive the transform from here instead.
  lists.forEach((list) => { list.style.animation = 'none'; });
  track.querySelectorAll('img').forEach((img) => { img.draggable = false; });

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const speed = 0.4; // px per ~16ms frame, leftward — matches the old loop feel.

  let copyWidth = lists[0].offsetWidth; // width of one full copy of the list
  let offset = 0;
  let hovered = false;
  let dragging = false;
  let startX = 0;
  let startOffset = 0;
  let pointerId = null;
  let last = performance.now();

  const apply = () => {
    const value = `translateX(${offset}px)`;
    lists.forEach((list) => { list.style.transform = value; });
  };

  // Keep offset within (-copyWidth, 0] so the duplicated list loops seamlessly
  // whichever way it moves.
  const wrap = () => {
    if (copyWidth <= 0) return;
    offset %= copyWidth;
    if (offset > 0) offset -= copyWidth;
  };

  const frame = (now) => {
    const elapsed = now - last;
    last = now;
    if (!hovered && !dragging && !reduceMotion) {
      offset -= speed * (elapsed / 16.6667);
      wrap();
      apply();
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  track.addEventListener('mouseenter', () => { hovered = true; });
  track.addEventListener('mouseleave', () => { hovered = false; });

  track.addEventListener('pointerdown', (event) => {
    dragging = true;
    startX = event.clientX;
    startOffset = offset;
    pointerId = event.pointerId;
    track.setPointerCapture(pointerId);
    ticker.classList.add('is-dragging');
  });

  track.addEventListener('pointermove', (event) => {
    if (!dragging) return;
    offset = startOffset + (event.clientX - startX);
    wrap();
    apply();
  });

  const endDrag = () => {
    if (!dragging) return;
    dragging = false;
    ticker.classList.remove('is-dragging');
    if (pointerId !== null && track.hasPointerCapture(pointerId)) {
      track.releasePointerCapture(pointerId);
    }
    pointerId = null;
  };
  track.addEventListener('pointerup', endDrag);
  track.addEventListener('pointercancel', endDrag);

  window.addEventListener('resize', () => { copyWidth = lists[0].offsetWidth; });
}

// ─── Persian digit localisation ─────────────────────────────────────────────────
// Convert Latin digits (0-9) to Persian (۰-۹) in all visible text, including
// content rendered later by Alpine / htmx / WooCommerce AJAX.
const CHRZ_PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
// Tags whose text content must keep Latin digits (code samples, form fields).
const CHRZ_DIGIT_SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'CODE', 'PRE', 'KBD', 'SAMP']);

function chrzToPersianDigits(text) {
  return text.replace(/[0-9]/g, (digit) => CHRZ_PERSIAN_DIGITS[digit]);
}

function chrzDigitNodeSkipped(textNode) {
  let el = textNode.parentElement;
  while (el) {
    if (CHRZ_DIGIT_SKIP_TAGS.has(el.tagName) || el.isContentEditable) return true;
    el = el.parentElement;
  }
  return false;
}

function chrzLocalizeTextNode(textNode) {
  const value = textNode.nodeValue;
  if (!value || !/[0-9]/.test(value)) return;
  if (chrzDigitNodeSkipped(textNode)) return;
  const converted = chrzToPersianDigits(value);
  // Converted text has no Latin digits, so a re-triggered mutation is a no-op:
  // this prevents the MutationObserver from looping on its own writes.
  if (converted !== value) textNode.nodeValue = converted;
}

function chrzLocalizeDigits(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    chrzLocalizeTextNode(node);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  if (CHRZ_DIGIT_SKIP_TAGS.has(node.tagName)) return;
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
  let current;
  while ((current = walker.nextNode())) {
    chrzLocalizeTextNode(current);
  }
}

function initPersianDigits() {
  chrzLocalizeDigits(document.body);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'characterData') {
        chrzLocalizeTextNode(mutation.target);
      } else {
        mutation.addedNodes.forEach(chrzLocalizeDigits);
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });
}

// ─── Init ──────────────────────────────────────────────────────────────────────
function init() {
  initPerformanceChart();
  initMarketTicker();
  initPersianDigits();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
